import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../../components/layout';
import { Card, Select, Button } from '../../components/ui';
import { dashboardApi, ApiError } from '../../api';
import type { DashboardFilterType, DashboardResponse, TopProvider } from '../../api';

interface DateRange {
	label: string;
	value: DashboardFilterType;
}

const dateRanges: DateRange[] = [
	{ label: 'All', value: 'ALL' },
	{ label: 'Today', value: 'TODAY' },
	{ label: 'This Week', value: 'THIS_WEEK' },
	{ label: 'Month To Date', value: 'MONTH_TO_DATE' },
	{ label: 'Year to Date', value: 'YEAR_TO_DATE' },
	{ label: 'Customize', value: 'CUSTOM' },
];

const SuperAdminDashboard = () => {
	const navigate = useNavigate();
	const [selectedDateRange, setSelectedDateRange] = useState<DashboardFilterType>('ALL');
	const [customDates, setCustomDates] = useState({ from: '', to: '' });
	const [isLoading, setIsLoading] = useState(true);
	const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);

	const showCustomDates = selectedDateRange === 'CUSTOM';

	// Get the label for selected filter
	const getFilterLabel = () => {
		const range = dateRanges.find(r => r.value === selectedDateRange);
		return range?.label || 'Today';
	};

	// Fetch dashboard data
	const fetchDashboardData = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await dashboardApi.getStats({
				filter: selectedDateRange,
				startDate: selectedDateRange === 'CUSTOM' ? customDates.from : undefined,
				endDate: selectedDateRange === 'CUSTOM' ? customDates.to : undefined,
			});
			setDashboardData(data);
		} catch (error) {
			if (error instanceof ApiError) {
				toast.error(error.message || 'Failed to load dashboard data');
			} else {
				toast.error('Failed to load dashboard data');
			}
		} finally {
			setIsLoading(false);
		}
	}, [selectedDateRange, customDates.from, customDates.to]);

	// Fetch data on mount and when filters change
	useEffect(() => {
		// For custom dates, only fetch when both dates are set
		if (selectedDateRange === 'CUSTOM') {
			if (customDates.from && customDates.to) {
				fetchDashboardData();
			}
		} else {
			fetchDashboardData();
		}
	}, [selectedDateRange, customDates.from, customDates.to, fetchDashboardData]);

	// Handle provider click
	const handleProviderClick = (provider: TopProvider) => {
		navigate(`/providers/${provider.id}`);
	};

	return (
		<AppLayout>
			{/* Filter Bar */}
			<Card className="mb-6">
				<div className="flex flex-wrap gap-4 items-center">
					<Select
						value={selectedDateRange}
						onChange={e => setSelectedDateRange(e.target.value as DashboardFilterType)}
						className="w-full sm:w-auto sm:min-w-[160px]"
					>
						{dateRanges.map(range => (
							<option key={range.value} value={range.value}>
								{range.label}
							</option>
						))}
					</Select>

					{showCustomDates && (
						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
							<input
								type="date"
								value={customDates.from}
								onChange={e =>
									setCustomDates(prev => ({ ...prev, from: e.target.value }))
								}
								className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer w-full sm:w-auto sm:min-w-[160px] hover:border-indigo-500 transition-colors"
							/>
							<span className="text-gray-500 dark:text-gray-400 text-sm text-center">
								to
							</span>
							<input
								type="date"
								value={customDates.to}
								onChange={e =>
									setCustomDates(prev => ({ ...prev, to: e.target.value }))
								}
								className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer w-full sm:w-auto sm:min-w-[160px] hover:border-indigo-500 transition-colors"
							/>
						</div>
					)}
				</div>
			</Card>

			{/* KPI Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
				<Card hover>
					<div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
						Bookings ({getFilterLabel()})
					</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
						{isLoading ? '...' : (dashboardData?.totalAppointments ?? 0)}
					</div>
				</Card>

				<Card hover>
					<div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
						Revenue
					</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
						{isLoading
							? '...'
							: `$${(dashboardData?.totalRevenue ?? 0).toLocaleString()}`}
					</div>
				</Card>

				<Card hover>
					<div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
						Total Customers
					</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
						{isLoading ? '...' : (dashboardData?.totalCustomers ?? 0)}
					</div>
				</Card>

				<Card hover>
					<div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
						Total Providers
					</div>
					<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
						{isLoading ? '...' : (dashboardData?.totalProviders ?? 0)}
					</div>
				</Card>
			</div>

			{/* Top Providers Section */}
			<Card>
				<div className="flex justify-between items-center mb-5">
					<h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 m-0">
						Top Providers (by Revenue)
					</h2>
					<Button size="sm" variant="primary" onClick={() => navigate('/providers')}>
						View All
					</Button>
				</div>

				<div className="flex flex-col gap-3">
					{isLoading ? (
						<div className="text-center py-8 text-gray-500 dark:text-gray-400">
							Loading providers...
						</div>
					) : dashboardData?.topProviders && dashboardData.topProviders.length > 0 ? (
						dashboardData.topProviders.map(provider => (
							<div
								key={provider.id}
								className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl flex justify-between items-center cursor-pointer transition-all duration-300 border-l-[3px] border-transparent hover:bg-indigo-50 dark:hover:bg-gray-600 hover:border-l-indigo-500 hover:translate-x-1"
								onClick={() => handleProviderClick(provider)}
							>
								<div className="flex-1">
									<div className="font-bold text-gray-900 dark:text-gray-100 mb-1">
										{provider.name}
									</div>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										{provider.totalAppointments} appointments
										{provider.businessName && ` · ${provider.businessName}`}
									</div>
								</div>
								<div className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
									${provider.totalRevenue.toLocaleString()}
								</div>
							</div>
						))
					) : (
						<div className="text-center py-8 text-gray-500 dark:text-gray-400">
							No providers found for this period
						</div>
					)}
				</div>
			</Card>

			{/* Footer */}
			<div className="mt-8 text-sm text-gray-500 dark:text-gray-400 text-center">
				© Beauty Business Portal
			</div>
		</AppLayout>
	);
};

export default SuperAdminDashboard;
